import { useState } from "react";
import { CreditCard, Plus, X, Trash2, Star, Loader2, Save } from "lucide-react";
import {
  useFormasPago,
  useCreateFormaPago,
  useUpdateFormaPago,
  useDeleteFormaPago,
} from "../api/formas-pago";
import type { FormaPagoFormData } from "../types/forma-pago";

interface Props {
  clienteId: number;
  clienteNombre: string;
  onClose: () => void;
}

const EMPTY_FORM: Omit<FormaPagoFormData, "cliente_id"> = {
  etiqueta: "",
  plazo_dias: null,
  iban: "",
  notas: "",
  es_predeterminada: false,
};

export function FormasPagoManager({ clienteId, clienteNombre, onClose }: Props) {
  const { data: formas, isLoading } = useFormasPago(clienteId);
  const createMut = useCreateFormaPago();
  const updateMut = useUpdateFormaPago();
  const deleteMut = useDeleteFormaPago();

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<typeof EMPTY_FORM>(EMPTY_FORM);

  function openAdd() {
    setForm(EMPTY_FORM);
    setAdding(true);
    setEditingId(null);
  }
  function openEdit(fpId: number) {
    const fp = formas?.find((x) => x.id === fpId);
    if (!fp) return;
    setForm({
      etiqueta: fp.etiqueta,
      plazo_dias: fp.plazo_dias,
      iban: fp.iban ?? "",
      notas: fp.notas ?? "",
      es_predeterminada: fp.es_predeterminada,
    });
    setEditingId(fpId);
    setAdding(false);
  }
  function cancelForm() {
    setAdding(false);
    setEditingId(null);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (editingId) {
      await updateMut.mutateAsync({ id: editingId, payload: form });
    } else {
      await createMut.mutateAsync({
        ...form,
        cliente_id: clienteId,
      });
    }
    cancelForm();
  }

  const showingForm = adding || editingId !== null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-6 my-8">
        <header className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
              <CreditCard size={22} className="text-brand-700" />
              Formas de pago
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">{clienteNombre}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
          >
            <X size={20} />
          </button>
        </header>

        {isLoading && (
          <div className="flex items-center gap-2 text-slate-500 py-4">
            <Loader2 className="animate-spin" size={16} /> Cargando…
          </div>
        )}

        {formas && (
          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden mb-4">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Etiqueta</th>
                  <th className="text-right px-3 py-2">Plazo</th>
                  <th className="text-left px-3 py-2">IBAN</th>
                  <th className="text-center px-3 py-2">Pred.</th>
                  <th className="text-right px-3 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {formas.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-slate-500">
                      Sin formas de pago configuradas.
                    </td>
                  </tr>
                )}
                {formas.map((fp) => (
                  <tr key={fp.id} className="border-t border-slate-200 bg-white">
                    <td className="px-3 py-2 font-bold text-slate-900">
                      {fp.etiqueta}
                      {fp.notas && (
                        <div className="text-xs text-slate-500 font-normal">
                          {fp.notas}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {fp.plazo_dias ? `${fp.plazo_dias} días` : "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{fp.iban ?? "—"}</td>
                    <td className="px-3 py-2 text-center">
                      {fp.es_predeterminada && (
                        <Star
                          size={14}
                          className="text-amber-500 inline"
                          fill="currentColor"
                        />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(fp.id)}
                          className="text-xs font-bold text-brand-700 hover:text-brand-900 px-2 py-1 rounded hover:bg-brand-50"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Eliminar forma de pago "${fp.etiqueta}"?`)) {
                              deleteMut.mutate(fp.id);
                            }
                          }}
                          className="p-1 rounded hover:bg-rose-50 text-rose-700"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!showingForm ? (
          <button
            type="button"
            onClick={openAdd}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 hover:border-brand-700 hover:bg-brand-50 px-4 py-3 text-sm font-bold text-slate-700"
          >
            <Plus size={16} /> Añadir forma de pago
          </button>
        ) : (
          <form
            onSubmit={save}
            className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-3"
          >
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
              {editingId ? "Editar" : "Nueva forma de pago"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Etiqueta *
                </label>
                <input
                  required
                  type="text"
                  value={form.etiqueta}
                  onChange={(e) => setForm({ ...form, etiqueta: e.target.value })}
                  placeholder="ej: Transferencia 30 días"
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Plazo (días)
                </label>
                <input
                  type="number"
                  min={0}
                  max={365}
                  value={form.plazo_dias ?? ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      plazo_dias: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  IBAN
                </label>
                <input
                  type="text"
                  value={form.iban ?? ""}
                  onChange={(e) => setForm({ ...form, iban: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                  placeholder="ES00 0000 0000 0000 0000 0000"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Notas
                </label>
                <input
                  type="text"
                  value={form.notas ?? ""}
                  onChange={(e) => setForm({ ...form, notas: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.es_predeterminada ?? false}
                    onChange={(e) =>
                      setForm({ ...form, es_predeterminada: e.target.checked })
                    }
                    className="rounded border-slate-300 text-brand-700 focus:ring-brand-600"
                  />
                  <span className="font-bold text-slate-700">
                    Marcar como predeterminada
                  </span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={cancelForm}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm font-bold hover:bg-white"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={createMut.isPending || updateMut.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-3 py-1.5 text-sm font-bold disabled:opacity-50"
              >
                <Save size={14} /> Guardar
              </button>
            </div>
          </form>
        )}

        <div className="mt-4 text-right">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-bold text-slate-600 hover:text-slate-900"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
